import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import deepmerge from 'deepmerge';
import { ReportInfo, ReportRecord, RecordUpdateEmission, TemplateProjectionMap } from '../../../models/report.model';
import { RawColumn } from '../../../models/template.model';
import { EDITABLE_COLUMNS_BY_ID } from './data-view-table.config';

@Component({
  selector: 'rep-data-view-table',
  templateUrl: './data-view-table.component.html',
  styleUrls: ['./data-view-table.component.scss'],
  standalone: false,
})
export class DataViewTableComponent implements OnChanges {
  reportRecordData: ReportRecord[];
  draggedRowIndex: number;
  allColumns: any[];
  currentFilteredField: RawColumn;
  actionsWord = {
    1: 'Actions',
    3: '操作',
  };
  actionHeader = this.actionsWord[1];
  showConfidentialityComponent = false;

  @Input() reportData: ReportInfo;
  @Input() displayedColumns: RawColumn[];
  @Input() reportLocale: number;
  @Input() defaultColumns: RawColumn[];
  @Input() templateProjectionMap: TemplateProjectionMap;

  @Output() reportUpdateClick: EventEmitter<RecordUpdateEmission> = new EventEmitter<RecordUpdateEmission>();
  @Output() reportReordered: EventEmitter<ReportRecord[]> = new EventEmitter<ReportRecord[]>();
  @Output() removeRecord: EventEmitter<ReportRecord> = new EventEmitter<ReportRecord>();
  @Output() reorderReportColumns: EventEmitter<number[]> = new EventEmitter<number[]>();

  ngOnChanges({ displayedColumns, reportData, defaultColumns }: SimpleChanges) {
    if (!!reportData && !!reportData.currentValue) {
      this.reportRecordData = deepmerge([], this.reportData ? this.reportData.records : null);
    }
    if (!!displayedColumns && displayedColumns.currentValue && displayedColumns.currentValue.length) {
      this.allColumns = this.setEditableColumns(displayedColumns.currentValue);
    } else if (!!defaultColumns && defaultColumns.currentValue) {
      this.allColumns = this.setEditableColumns(this.defaultColumns);
    }
    this.actionHeader = this.actionsWord[this.reportLocale] ? this.actionsWord[this.reportLocale] : this.actionsWord[1];
    this.showConfidentialityComponent =
      !!this.templateProjectionMap && !!this.templateProjectionMap['miq-confidentiality-control-tmpl'];
  }

  dragStartColumn(startIndex: number) {
    this.draggedRowIndex = startIndex;
  }

  dropColumn(droppedIndex: number) {
    if (this.draggedRowIndex != droppedIndex) {
      // Update positions, the drag and drop handle to put the item in the right place
      this.reportRecordData = this.reportRecordData.map((item, index) => ({ ...item, position: index + 1 }));
      this.reportReordered.emit(this.reportRecordData);
    }
  }

  emitEditableFields(event: MouseEvent, field: { [key: string]: any }, record: ReportRecord) {
    if (this.isColumnEditable(field as RawColumn)) {
      let emittedObject: any;
      const edpUpdateConf = {
        labelMode: !!field.fieldUOM ? 'associated' : 'normal',
        updatableFields: [field.field, field.fieldUOM].filter(element => !!element),
      };
      emittedObject = {
        event,
        edpUpdateConf: edpUpdateConf,
        record: record.data,
      };
      if (emittedObject != null) {
        emittedObject.locale = this.reportLocale;
        this.reportUpdateClick.emit(emittedObject);
      }
    }
  }

  setEditableColumns(columns: RawColumn[]) {
    return columns.map(col => ({ ...col, isEditable: this.isColumnEditable(col) }));
  }

  isColumnEditable(col: RawColumn) {
    // Update if the field has a parent configuration and it is not part of an array
    return EDITABLE_COLUMNS_BY_ID[col.columnId] ?? !!(col.parent?.update && !col.field.includes('/'));
  }

  reorderColumns(columns: RawColumn[]) {
    const columnsIDs: number[] = columns.map(({ columnId }) => columnId);
    this.reorderReportColumns.emit(columnsIDs);
  }
}
